import type { Editor } from "./canvas";
import {
  draftingDefaults,
  type DraftingKind,
  type DraftingProps,
} from "../shared/drafting";

type LengthKey = "width" | "thickness";
type Defaults = {
  style: DraftingProps;
  millimeters: Partial<Record<LengthKey, number>>;
};
const defaults = new WeakMap<Editor, Map<string, Defaults>>();

function defaultsFor(editor: Editor, kind: string) {
  let map = defaults.get(editor);
  if (!map) {
    map = new Map();
    defaults.set(editor, map);
  }
  if (!map.has(kind))
    map.set(kind, {
      style: {
        ...structuredClone(draftingDefaults),
        kind: kind as DraftingKind,
      },
      millimeters: {},
    });
  return map.get(kind)!;
}

export function draftingStyle(editor: Editor, kind: string) {
  return defaultsFor(editor, kind).style;
}

/** Keep physical defaults in mm until the actual drawing parent is known. */
export function setDraftingLength(
  editor: Editor,
  kind: string,
  key: LengthKey,
  value: number,
  metersPerUnit: number,
) {
  const defaults = defaultsFor(editor, kind);
  const factor = metersPerUnit > 0 ? metersPerUnit * 1000 : 1;
  defaults.style[key] = Math.min(5000, value / factor);
  if (metersPerUnit > 0)
    defaults.millimeters[key] = defaults.style[key] * factor;
  else delete defaults.millimeters[key];
}

export function resolvedDraftingStyle(
  editor: Editor,
  kind: string,
  metersPerUnit: number,
): DraftingProps {
  const defaults = defaultsFor(editor, kind);
  const style = { ...defaults.style };
  if (metersPerUnit > 0)
    for (const key of ["width", "thickness"] as const) {
      const millimeters = defaults.millimeters[key];
      if (millimeters !== undefined)
        style[key] = Math.min(5000, millimeters / (metersPerUnit * 1000));
    }
  return style;
}
